import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '@/lib/api';

/**
 * One small fetch hook shared by every settings screen.
 *
 * Settings pages are read-then-edit-then-reload; none of them need polling, so
 * this is deliberately simpler than `useDevices` rather than a copy of it.
 */
export function useResource<T>(path: string | null) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (path === null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setData(await api.get<T>(path));
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load this page.');
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, reload: load, setData };
}

export interface FieldError {
  field: string;
  message: string;
}

/**
 * Normalises an API failure into the per-field list the forms render.
 *
 * The server returns `details` keyed by field path from ZodPipe, so the same
 * zod schema that validated the body also highlights the offending input.
 * Hand-thrown refusals do the same — `media_kind_mismatch` names
 * `heroMediaAssetId`, so choosing an image for the kiosk hero video lands on
 * that field rather than as a page-level "save failed".
 *
 * A detail that names a field but carries no message falls back to the
 * exception's own message: a highlighted input with no text beside it is a
 * silent failure, which is the exact shape of bug this rewrites.
 */
export function toFieldErrors(caught: unknown, fallback: string): FieldError[] {
  if (caught instanceof ApiError && Array.isArray(caught.details)) {
    return (caught.details as Partial<FieldError>[]).map((detail) => ({
      field: detail.field ?? '(root)',
      message: detail.message || caught.message || fallback,
    }));
  }
  return [{ field: '(root)', message: caught instanceof Error ? caught.message : fallback }];
}
